import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { mediaFileUrl } from '../mediaFileUrl';
import { HOME, ABOUT, CONTACT, PAGE_DESCRIPTIONS, SITE_ORIGIN } from './siteContent';
import { PrivacyPage, TermsPage, formatAbn } from './LegalPages';
import TradeEnquiryPage from './TradeEnquiryPage';
import ApplicationPage from './ApplicationPage';
import ContactEnquiryForm from './ContactEnquiryForm';
import './site.css';

// The public website — Home, About, Contact, Privacy and Terms — shown to
// anyone before they sign in, with the doors into the Customer portal
// (/portal) and the back office (/staff). Sits outside BrowserRouter (see
// AuthGate.js), so it moves between its own pages with history.pushState;
// the portal and staff links are full page loads, since AuthGate decides
// what a page load shows.

const IS_DEV = process.env.NODE_ENV !== 'production';

const PAGES = {
  '/': { title: null, description: PAGE_DESCRIPTIONS.home, Component: HomePage },
  '/brands': { title: 'Brands', description: PAGE_DESCRIPTIONS.brands, Component: BrandsPage },
  '/about': { title: 'About', description: PAGE_DESCRIPTIONS.about, Component: AboutPage },
  '/contact': { title: 'Contact', description: PAGE_DESCRIPTIONS.contact, Component: ContactPage },
  '/trade-enquiry': { title: 'Become a stockist', description: PAGE_DESCRIPTIONS.tradeEnquiry, Component: TradeEnquiryPage },
  // A Trading or Credit Account Application's private link (issue #290).
  '/apply': { title: 'Account application', description: PAGE_DESCRIPTIONS.application, Component: ApplicationPage },
  '/privacy': { title: 'Privacy Policy', description: PAGE_DESCRIPTIONS.privacy, Component: PrivacyPage },
  '/terms': { title: 'Terms of Use', description: PAGE_DESCRIPTIONS.terms, Component: TermsPage },
  '/quickbooks-disconnected': { title: 'QuickBooks disconnected', description: PAGE_DESCRIPTIONS.quickbooksDisconnected, Component: QuickBooksDisconnectedPage },
};

// A brand's own page (issue #285): /brands/<its web address>.
const BRAND_PAGE = /^\/brands\/([^/]+)$/;

// The page an address shows, or null when it isn't a website address.
// A trailing slash is ignored (/about/ is About). Any /brands/<name> is a
// website address: the old WooCommerce site's brand addresses redirect
// there (issue #366), and one the site doesn't know shows the Brands page.
export function sitePageFor(pathname) {
  const path = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;
  if (PAGES[path]) return path;
  const brand = path.match(BRAND_PAGE);
  if (brand) return `/brands/${brand[1].toLowerCase()}`;
  return null;
}

function brandSlugOf(path) {
  const match = path.match(BRAND_PAGE);
  return match ? match[1] : null;
}

// A Brand's own website, as a link: stored as http(s) or a bare address.
function brandWebsiteHref(url) {
  return /^https?:\/\//i.test(url) ? url : `https://${url}`;
}

// The search-result description for a brand's page: who distributes it,
// then as much of its description as fits.
function brandMetaDescription(brand, name) {
  const lead = name
    ? `${name} is the Australian and New Zealand distributor of ${brand.name}.`
    : `${brand.name}, distributed in Australia and New Zealand.`;
  const text = `${lead} ${brand.description.split('\n')[0]}`.trim();
  return text.length > 160 ? `${text.slice(0, 157).replace(/\s+\S*$/, '')}…` : text;
}

// The search-result description for the page being shown.
function setMetaDescription(text) {
  let tag = document.querySelector('meta[name="description"]');
  if (!tag) {
    tag = document.createElement('meta');
    tag.setAttribute('name', 'description');
    document.head.appendChild(tag);
  }
  tag.setAttribute('content', text);
}

// The page's one address on kronigsupply.com.au, so search engines don't
// split its ranking across addresses.
function setCanonical(path) {
  let tag = document.querySelector('link[rel="canonical"]');
  if (!tag) {
    tag = document.createElement('link');
    tag.setAttribute('rel', 'canonical');
    document.head.appendChild(tag);
  }
  tag.setAttribute('href', SITE_ORIGIN + path);
}

// Where every "Become a stockist" button goes: the Trade Enquiry form
// (issue #286).
const STOCKIST_PATH = '/trade-enquiry';

const NAV = [
  { path: '/', label: 'Home' },
  { path: '/brands', label: 'Brands' },
  { path: '/about', label: 'About' },
  { path: '/contact', label: 'Contact' },
];

let profileRequest = null;
function loadProfile() {
  if (!profileRequest) {
    profileRequest = axios.get('/api/site/profile')
      .then((res) => res.data.data)
      .catch(() => { profileRequest = null; return null; });
  }
  return profileRequest;
}

// The Brands shown on the website (GET /api/site/brands), in display order.
let brandsRequest = null;
function loadBrands() {
  if (!brandsRequest) {
    brandsRequest = axios.get('/api/site/brands')
      .then((res) => res.data.data || [])
      .catch(() => { brandsRequest = null; return []; });
  }
  return brandsRequest;
}

function shortName(profile) {
  return (profile?.company_name || '').replace(/\s+Pty\.?\s+Ltd\.?$/i, '');
}

function PublicSite() {
  const [path, setPath] = useState(() => sitePageFor(window.location.pathname) || '/');
  const [profile, setProfile] = useState(null);
  const [brands, setBrands] = useState(null);
  const [logoFailed, setLogoFailed] = useState(false);

  // /about/ shows as /about, the page's one address.
  useEffect(() => {
    const { pathname, search, hash } = window.location;
    if (pathname.length > 1 && pathname.endsWith('/')) {
      window.history.replaceState(null, '', pathname.replace(/\/+$/, '') + search + hash);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    loadProfile().then((data) => { if (!cancelled) setProfile(data); });
    loadBrands().then((data) => { if (!cancelled) setBrands(data); });
    return () => { cancelled = true; };
  }, []);

  // A brand address the site doesn't show (an old brand such as Canare, or
  // one since taken off the website) lands on the Brands page.
  const slug = brandSlugOf(path);
  const brand = slug && brands ? brands.find((b) => b.slug === slug) : null;
  useEffect(() => {
    if (slug && brands && !brand) {
      window.history.replaceState(null, '', '/brands');
      setPath('/brands');
    }
  }, [slug, brands, brand]);

  useEffect(() => {
    const onPop = () => setPath(sitePageFor(window.location.pathname) || '/');
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  const name = shortName(profile);
  const page = slug
    ? { title: brand?.name, description: brand ? brandMetaDescription(brand, name) : PAGE_DESCRIPTIONS.brands, Component: brand ? BrandPage : LoadingPage }
    : PAGES[path];
  useEffect(() => {
    const base = [page.title, name].filter(Boolean).join(' · ') || 'Welcome';
    document.title = `${IS_DEV ? '[DEV] ' : ''}${base}`;
    setMetaDescription(page.description);
    setCanonical(path);
  }, [path, name, page.title, page.description]);

  const go = useCallback((to) => (event) => {
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1) return;
    event.preventDefault();
    if (to !== window.location.pathname) window.history.pushState(null, '', to);
    setPath(to);
    window.scrollTo(0, 0);
  }, []);

  const { Component } = page;
  // The Customer portal buttons show only while Platform Settings' Show
  // Customer portal on website is on (issue #288); customers already using
  // the portal have their own link either way.
  const portalHref = profile?.show_portal ? (profile.portal_path || '/portal') : null;

  return (
    <div className="ks-site">
      {IS_DEV && <div className="ks-dev-bar">DEVELOPMENT</div>}
      <header className="ks-header">
        <div className="ks-wrap ks-header-inner">
          <a href="/" className="ks-brand" onClick={go('/')} aria-label={`${name || 'Home'} home`}>
            {profile?.logo_id && !logoFailed
              ? <img src={mediaFileUrl(profile.logo_id)} alt={name} onError={() => setLogoFailed(true)} />
              : <span>{name}</span>}
          </a>
          <nav className="ks-nav" aria-label="Main">
            {NAV.map((item) => (
              <a key={item.path} href={item.path} onClick={go(item.path)} aria-current={path === item.path || (item.path === '/brands' && slug) ? 'page' : undefined}>
                {item.label}
              </a>
            ))}
          </nav>
          <div className="ks-header-actions">
            {portalHref && <a href={portalHref} className="ks-link-quiet">Customer portal</a>}
            <a href={STOCKIST_PATH} onClick={go(STOCKIST_PATH)} className="ks-btn ks-btn-solid ks-btn-small">Become a stockist</a>
          </div>
        </div>
      </header>

      <main>
        <Component profile={profile} name={name} brands={brands || []} brand={brand} go={go} portalHref={portalHref} />
      </main>

      <footer className="ks-footer ks-on-dark">
        <div className="ks-wrap">
          <div className="ks-footer-grid">
            <div>
              <p className="ks-footer-name">{name}</p>
              {profile?.about_us && <p className="ks-footer-about">{profile.about_us.split('\n')[0]}</p>}
            </div>
            <div>
              <h4>Company</h4>
              <ul>
                <li><a href="/about" onClick={go('/about')}>About</a></li>
                <li><a href="/contact" onClick={go('/contact')}>Contact</a></li>
                {profile?.instagram_url && <li><a href={profile.instagram_url} target="_blank" rel="noopener noreferrer">Instagram</a></li>}
              </ul>
            </div>
            <div>
              <h4>Trade</h4>
              <ul>
                <li><a href={STOCKIST_PATH} onClick={go(STOCKIST_PATH)}>Become a stockist</a></li>
                {portalHref && <li><a href={portalHref}>Customer portal</a></li>}
                <li><a href="/staff">Staff login</a></li>
              </ul>
            </div>
            <div>
              <h4>Legal</h4>
              <ul>
                <li><a href="/privacy" onClick={go('/privacy')}>Privacy Policy</a></li>
                <li><a href="/terms" onClick={go('/terms')}>Terms of Use</a></li>
              </ul>
            </div>
          </div>
          <div className="ks-footer-base">
            <span>© {new Date().getFullYear()} {profile?.company_name}{profile?.abn ? ` · ABN ${formatAbn(profile.abn)}` : ''}</span>
            {profile?.email && <a href={`mailto:${profile.email}`}>{profile.email}</a>}
          </div>
        </div>
      </footer>
    </div>
  );
}

function HomePage({ brands, go, portalHref }) {
  return (
    <>
      <section className="ks-section">
        <div className="ks-wrap ks-hero">
          <div>
            <p className="ks-eyebrow">{HOME.eyebrow}</p>
            <h1 className="ks-display">{HOME.headline}</h1>
            <p className="ks-lead">{HOME.intro}</p>
            <div className="ks-btn-row">
              <a href={STOCKIST_PATH} onClick={go(STOCKIST_PATH)} className="ks-btn ks-btn-solid">Become a stockist</a>
              <a href="/about" className="ks-btn ks-btn-ghost" onClick={go('/about')}>Our story</a>
            </div>
          </div>
          <aside className="ks-spec" aria-label="At a glance">
            <p className="ks-spec-title">At a glance</p>
            <dl>
              {HOME.facts.map((fact) => (
                <div className="ks-spec-row" key={fact.label}>
                  <dt>{fact.label}</dt>
                  <dd>{fact.value}</dd>
                </div>
              ))}
            </dl>
          </aside>
        </div>
      </section>

      <section className="ks-section ks-section-mist">
        <div className="ks-wrap ks-grid-3">
          {HOME.pillars.map((pillar, i) => (
            <div className="ks-pillar" key={pillar.title}>
              <div className="ks-pillar-num">0{i + 1}</div>
              <h3>{pillar.title}</h3>
              <p>{pillar.body}</p>
            </div>
          ))}
        </div>
      </section>

      {brands.length > 0 && (
        <section className="ks-section">
          <div className="ks-wrap ks-split">
            <div>
              <p className="ks-eyebrow">Brands we distribute</p>
              <h2 className="ks-h2">A curated range.</h2>
              <p style={{ color: 'var(--muted)', margin: 0 }}>{HOME.brandsIntro}</p>
            </div>
            <ul className="ks-brand-list">
              {brands.map((brand, i) => (
                <li key={brand.slug}>
                  <a href={`/brands/${brand.slug}`} onClick={go(`/brands/${brand.slug}`)}>
                    <span>{String(i + 1).padStart(2, '0')}</span>{brand.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}

      <section className="ks-section ks-section-ink ks-on-dark">
        <div className="ks-wrap ks-cta">
          <div>
            <h2 className="ks-h2">{HOME.cta.title}</h2>
            <p>{HOME.cta.body}</p>
          </div>
          <div className="ks-btn-row">
            <a href={STOCKIST_PATH} onClick={go(STOCKIST_PATH)} className="ks-btn ks-btn-solid">Become a stockist</a>
            <a href="/contact" className="ks-btn ks-btn-ghost" onClick={go('/contact')}>Contact us</a>
            {portalHref && <a href={portalHref} className="ks-btn ks-btn-ghost">Customer portal</a>}
          </div>
        </div>
      </section>
    </>
  );
}

function BrandLogo({ brand, className }) {
  const [failed, setFailed] = useState(false);
  if (!brand.logo_id || failed) return <span className={`${className} ks-brand-logo-text`}>{brand.name}</span>;
  return <img className={className} src={mediaFileUrl(brand.logo_id)} alt={brand.name} onError={() => setFailed(true)} />;
}

function BrandsPage({ brands, go }) {
  return (
    <>
      <section className="ks-section ks-section-tight">
        <div className="ks-wrap">
          <p className="ks-eyebrow">Brands we distribute</p>
          <h1 className="ks-display">A curated range.</h1>
          <p className="ks-lead">{HOME.brandsIntro}</p>
        </div>
      </section>

      <section className="ks-section" style={{ paddingTop: 0 }}>
        <div className="ks-wrap ks-brand-grid">
          {brands.map((brand) => (
            <a key={brand.slug} href={`/brands/${brand.slug}`} onClick={go(`/brands/${brand.slug}`)} className="ks-brand-card">
              <div className="ks-brand-card-logo"><BrandLogo brand={brand} className="ks-brand-logo" /></div>
              <h2 className="ks-h3">{brand.name}</h2>
              <p>{brand.description.split('\n')[0]}</p>
              <span className="ks-brand-card-more">View brand →</span>
            </a>
          ))}
        </div>
      </section>

      <section className="ks-section ks-section-ink ks-on-dark">
        <div className="ks-wrap ks-cta">
          <div>
            <h2 className="ks-h2">{HOME.cta.title}</h2>
            <p>{HOME.cta.body}</p>
          </div>
          <div className="ks-btn-row">
            <a href={STOCKIST_PATH} onClick={go(STOCKIST_PATH)} className="ks-btn ks-btn-solid">Become a stockist</a>
          </div>
        </div>
      </section>
    </>
  );
}

function BrandPage({ brand, name, go }) {
  return (
    <>
      <section className="ks-section ks-section-tight">
        <div className="ks-wrap" style={{ maxWidth: 900 }}>
          <a href="/brands" onClick={go('/brands')} className="ks-back-link">← All brands</a>
          <div className="ks-brand-page-logo"><BrandLogo brand={brand} className="ks-brand-logo" /></div>
          <h1 className="ks-display">{brand.name}</h1>
          <p className="ks-lead">
            {name ? `${name} is the Australian and New Zealand distributor of ${brand.name}.` : 'Distributed in Australia and New Zealand.'}
          </p>
          <div className="ks-prose" style={{ marginTop: 24 }}>
            {brand.description.split(/\n+/).filter(Boolean).map((para) => <p key={para}>{para}</p>)}
          </div>
          <div className="ks-btn-row">
            <a href={STOCKIST_PATH} onClick={go(STOCKIST_PATH)} className="ks-btn ks-btn-solid">Become a stockist</a>
            {brand.url && (
              <a href={brandWebsiteHref(brand.url)} className="ks-btn ks-btn-ghost" target="_blank" rel="noopener noreferrer">
                Visit {brand.name}’s website
              </a>
            )}
          </div>
        </div>
      </section>
    </>
  );
}

// A brand address while the Brands list is still loading.
function LoadingPage() {
  return <section className="ks-section"><div className="ks-wrap" style={{ minHeight: '40vh' }} /></section>;
}

function AboutPage() {
  return (
    <>
      <section className="ks-section ks-section-tight">
        <div className="ks-wrap ks-split ks-split-even">
          <div>
            <p className="ks-eyebrow">{ABOUT.eyebrow}</p>
            <h1 className="ks-display">{ABOUT.headline}</h1>
            <p className="ks-lead">{ABOUT.lead}</p>
          </div>
          <div className="ks-story">
            <h2 className="ks-h3">{ABOUT.storyTitle}</h2>
            <div className="ks-prose">
              {ABOUT.story.map((para) => <p key={para}>{para}</p>)}
            </div>
          </div>
        </div>
      </section>

      <section className="ks-section ks-section-ink ks-on-dark">
        <div className="ks-wrap" style={{ maxWidth: 900 }}>
          <blockquote className="ks-quote">
            <p>“{ABOUT.quote.text}”</p>
            <footer><strong>{ABOUT.quote.name}</strong> · {ABOUT.quote.role}</footer>
          </blockquote>
        </div>
      </section>

      <section className="ks-section">
        <div className="ks-wrap ks-split">
          <div>
            <h2 className="ks-h2">{ABOUT.partnersTitle}</h2>
            <p style={{ color: 'var(--muted)', margin: 0 }}>{ABOUT.partnersIntro}</p>
          </div>
          <div className="ks-criteria">
            {ABOUT.partnerCriteria.map((item, i) => (
              <div className="ks-pillar" key={item.title}>
                <div className="ks-pillar-num">0{i + 1}</div>
                <h3>{item.title}</h3>
                <p>{item.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}

function ContactPage({ profile, go, portalHref }) {
  const place = [profile?.city, profile?.state].filter(Boolean).join(', ');
  return (
    <>
      <section className="ks-section">
        <div className="ks-wrap">
          <p className="ks-eyebrow">{CONTACT.eyebrow}</p>
          <h1 className="ks-display">{CONTACT.headline}</h1>
          <p className="ks-lead">{CONTACT.lead}</p>
        </div>
      </section>

      <section className="ks-section" style={{ paddingTop: 0 }}>
        <div className="ks-wrap">
          {/* The enquiry form beside the email and phone (issue #287). */}
          <div className="ks-split">
            <div className="ks-contact-stack">
              <div className="ks-contact-item">
                <div className="ks-contact-label">Email</div>
                <div className="ks-contact-value">{profile?.email ? <a href={`mailto:${profile.email}`}>{profile.email}</a> : '—'}</div>
              </div>
              <div className="ks-contact-item">
                <div className="ks-contact-label">Phone</div>
                <div className="ks-contact-value">{profile?.phone ? <a href={`tel:${(profile.phone_intl || profile.phone).replace(/[^\d+]/g, '')}`}>{profile.phone}</a> : '—'}</div>
              </div>
              <div className="ks-contact-item">
                <div className="ks-contact-label">Based in</div>
                <div className="ks-contact-value">{place || '—'}</div>
                <div className="ks-contact-note">{CONTACT.locationNote}</div>
              </div>
            </div>
            <ContactEnquiryForm copy={CONTACT} go={go} />
          </div>

          <div className="ks-panel" style={{ marginTop: 48 }}>
            <h2 className="ks-h2">{CONTACT.tradeTitle}</h2>
            <p style={{ color: 'var(--muted)', margin: 0, maxWidth: '62ch' }}>{CONTACT.tradeBody}</p>
            <div className="ks-btn-row" style={{ marginTop: 24 }}>
              <a href={STOCKIST_PATH} onClick={go(STOCKIST_PATH)} className="ks-btn ks-btn-solid">Send a trade enquiry</a>
              {portalHref && <a href={portalHref} className="ks-btn ks-btn-ghost">Customer portal sign in</a>}
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

// Where Intuit sends someone who disconnects Optim from inside QuickBooks
// (the app's Disconnect URL on the Intuit Developer portal).
function QuickBooksDisconnectedPage() {
  return (
    <section className="ks-section">
      <div className="ks-wrap" style={{ maxWidth: 760 }}>
        <p className="ks-eyebrow">QuickBooks</p>
        <h1 className="ks-display">QuickBooks has been disconnected.</h1>
        <p className="ks-lead">
          Optim no longer reads from or sends anything to your QuickBooks company. Records already in Optim and in
          QuickBooks are unchanged.
        </p>
        <p className="ks-lead">
          To connect again, sign in and go to <strong>Platform → API Integrations</strong>, then choose <strong>Connect</strong> on
          the QuickBooks card.
        </p>
        <div className="ks-btn-row">
          <a href="/global-api-integrations" className="ks-btn ks-btn-solid">Sign in to reconnect</a>
        </div>
      </div>
    </section>
  );
}

export default PublicSite;
